/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { EnvironmentalImpactByApplication } from "../models/http/application/environmental-impact-by-application.model";
import { BarChart } from "../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../models/http/charts/line-chart/line-chart.model";
import { PieChart } from "../models/http/charts/pie-chart/pie-chart.model";
import { StrategicApplicationFileStatusResponse } from "../models/http/strategic-applications/strategic-application-file-status-response.model";
import { StrategicApplicationFilesList } from "../models/http/strategic-applications/strategic-application-files-list.model";
import { StrategicApplicationGeneralInformation } from "../models/http/strategic-applications/strategic-application-general-information.model";
import { StrategicApplicationUploadResponse } from "../models/http/strategic-applications/strategic-application-upload-response.model";
import { ChartResponse } from "../models/ui/charts.model";
import { StrategicApplicationsHttpService } from "./http/strategic-applications-http.service";

@Injectable({
	providedIn: "root"
})
export class StrategicApplicationsService {
	constructor(private _strategicApplicationsHttpService: StrategicApplicationsHttpService) {}

	public getFiles(tenantId: string): Observable<StrategicApplicationFilesList> {
		return this._strategicApplicationsHttpService.getFiles(tenantId);
	}

	public uploadFile(
		tenantId: string,
		file: FormData,
		isTemporary?: boolean
	): Observable<StrategicApplicationUploadResponse> {
		return this._strategicApplicationsHttpService.uploadFile(tenantId, file, isTemporary);
	}

	public getFileStatus(
		tenantId: string,
		fileId: string
	): Observable<StrategicApplicationFileStatusResponse> {
		return this._strategicApplicationsHttpService.getFileStatus(tenantId, fileId);
	}

	public renameFile(tenantId: string, fileId: string, name: string): Observable<void> {
		return this._strategicApplicationsHttpService.renameFile(tenantId, fileId, name);
	}

	public deleteFile(tenantId: string, fileId: string): Observable<void> {
		return this._strategicApplicationsHttpService.deleteFile(tenantId, fileId);
	}

	public exportFile(tenantId: string, fileId: string): Observable<string> {
		return this._strategicApplicationsHttpService.exportFile(tenantId, fileId);
	}

	public getGeneralInformation(
		tenantId: string,
		fileId: string
	): Observable<StrategicApplicationGeneralInformation> {
		return this._strategicApplicationsHttpService.getGeneralInformation(tenantId, fileId);
	}

	public getEmissionApplications(
		tenantId: string,
		fileId: string
	): Observable<ChartResponse<BarChart>> {
		return this._strategicApplicationsHttpService.getEmissionApplications(tenantId, fileId);
	}

	public getEmissionByScope(
		tenantId: string,
		fileId: string
	): Observable<ChartResponse<PieChart>> {
		return this._strategicApplicationsHttpService.getEmissionByScope(tenantId, fileId);
	}

	public getEmissionByDeployment(
		tenantId: string,
		fileId: string
	): Observable<ChartResponse<PieChart>> {
		return this._strategicApplicationsHttpService.getEmissionByDeployment(tenantId, fileId);
	}

	public getTotalEmissionsOvertime(
		tenantId: string,
		fileId: string,
		startDate: string,
		endDate?: string
	): Observable<ChartResponse<LineChart>> {
		return this._strategicApplicationsHttpService.getTotalEmissionsOvertime(
			tenantId,
			fileId,
			startDate,
			endDate
		);
	}

	public downloadTemplate(level: string): Observable<Blob> {
		return this._strategicApplicationsHttpService.downloadTemplate(level);
	}

	public getEnvironmentalImpactByApplication(
		fileId: string,
		page = 0,
		size = 10
	): Observable<EnvironmentalImpactByApplication> {
		return this._strategicApplicationsHttpService.getEnvironmentalImpactByApplication(
			fileId,
			page,
			size
		);
	}
}
